// <ritual-scroll> — Cha Maker ritual (docs/10 FC-6).
// Desktop ≥1024: chapters open by click only (one at a time). Steps of the open chapter advance with
// scroll: progress eases toward its target (lerp 0.12 per 16.7 ms, time-based so 60/120 Hz feel the same), each step's media wipes up with
// transforms only (outer translateY 100%→0, inner counter-translate + scale 1.08→1); only the current
// and next layer are visible. Section height = 100vh + steps × 60vh of the open chapter.
// Phone ≤1023: chapter tabs; the open chapter's steps are a scroll-snap carousel with dots.
const desk = window.matchMedia('(min-width: 1024px)');
const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
const cl = (v) => Math.min(1, Math.max(0, v));
const ios = (v) => -(Math.cos(Math.PI * v) - 1) / 2;

class RitualScroll extends HTMLElement {
  connectedCallback() {
    this.ch = 0; this.p = null; this.ts = 0; this.inView = false;
    this.figs = [...this.querySelectorAll('.zr-f')];
    this.setChapter(0, false);

    this.querySelectorAll('.zr-h').forEach((h) => h.addEventListener('click', () => {
      const c = +h.closest('.zr-c').dataset.ch; if (c !== this.ch) this.setChapter(c, true);
    }));
    this.querySelectorAll('.zrm-tab').forEach((t) => t.addEventListener('click', () => this.setTab(+t.dataset.ch)));
    this.querySelector('.zrm-tabs')?.addEventListener('keydown', (e) => {
      const d = { ArrowRight: 1, ArrowLeft: -1 }[e.key]; if (!d) return;
      const tabs = [...this.querySelectorAll('.zrm-tab')]; const i = (this.mch + d + tabs.length) % tabs.length;
      e.preventDefault(); this.setTab(i); tabs[i].focus();
    });
    this.querySelectorAll('.zrm-track').forEach((tr) => tr.addEventListener('scroll', () => this.onTrack(tr), { passive: true }));
    this.mch = 0;

    this.onScroll = () => { if (!this.raf) this.raf = requestAnimationFrame(() => { this.raf = 0; this.frame(); }); };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll);
    this.io = new IntersectionObserver(([en]) => { this.inView = en.isIntersecting; this.onScroll(); this.playPhone(); }, { threshold: 0.15 });
    this.io.observe(this);
    desk.addEventListener('change', () => { this.p = null; this.onScroll(); this.playPhone(); });
    this.frame();
  }
  disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); window.removeEventListener('resize', this.onScroll); this.io?.disconnect(); }

  stepsOf(c) { return this.figs.filter((f) => +f.dataset.ch === c); }

  // desktop: the visible media of the outgoing chapter fades out above the incoming chapter's step 1 (420ms)
  fadeOut() {
    const leaving = this.stepsOf(this.ch).filter((f) => f.style.visibility === 'visible');
    this._leaving = new Set(leaving);
    leaving.forEach((f) => { f.classList.add('fx'); f.style.zIndex = '50'; f.style.opacity = '1'; });
    requestAnimationFrame(() => requestAnimationFrame(() => leaving.forEach((f) => { f.style.opacity = '0'; })));
    clearTimeout(this._lt);
    this._lt = setTimeout(() => {
      leaving.forEach((f) => { f.classList.remove('fx'); f.style.opacity = ''; f.style.visibility = 'hidden'; f.style.zIndex = ''; });
      this._leaving = null;
    }, 460);
  }

  setChapter(c, scroll) {
    if (scroll && desk.matches && !reduce.matches && this.p !== null) this.fadeOut();
    this.ch = c;
    const n = Math.max(1, this.stepsOf(c).length);
    this.style.setProperty('--steps', n);
    this.querySelectorAll('.zr-c').forEach((el) => {
      const on = +el.dataset.ch === c; el.classList.toggle('on', on);
      el.querySelector('.zr-h').setAttribute('aria-expanded', String(on));
    });
    const no = this.querySelectorAll('.zr-no')[c]?.textContent; const cnt = this.querySelector('[data-ch-no]'); if (cnt && no) cnt.textContent = no;
    this.figs.forEach((f) => { f._was = false; });
    this.p = null; this.ts = 0;
    if (scroll && desk.matches) {
      const hdr = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--hdr-h')) || 64;
      const top = this.getBoundingClientRect().top + window.scrollY - hdr + 1;
      window.scrollTo({ top, behavior: reduce.matches ? 'auto' : 'smooth' });
    }
    this.frame();
  }

  frame() {
    if (!desk.matches) { this.figs.forEach((f) => { const v = f.querySelector('video'); if (v && !v.paused) v.pause(); }); return; }
    const r = this.getBoundingClientRect();
    const span = r.height - window.innerHeight;
    const t = span > 0 ? Math.min(0.9999, Math.max(0, -r.top / span)) : 0;
    const now = performance.now();
    if (this.p === null || reduce.matches || Math.abs(t - this.p) > 0.4) { this.p = t; this.ts = 0; }
    else {
      const dt = this.ts ? Math.min(64, now - this.ts) : 1000 / 60;
      this.p += (t - this.p) * (1 - Math.pow(1 - 0.12, dt / (1000 / 60))); this.ts = now;
    }
    if (Math.abs(t - this.p) < 0.0003) { this.p = t; this.ts = 0; }
    else if (!this.raf) this.raf = requestAnimationFrame(() => { this.raf = 0; this.frame(); });

    const figs = this.stepsOf(this.ch); const n = figs.length;
    const x = this.p * n, W = 0.5;
    const c = Math.min(n - 1, Math.floor(x));
    this.figs.forEach((f) => { if (+f.dataset.ch !== this.ch && !this._leaving?.has(f)) { f.style.visibility = 'hidden'; const v = f.querySelector('video'); if (v && !v.paused) v.pause(); f._was = false; } });
    figs.forEach((f, i) => {
      let e = i === 0 ? 1 : ios(cl((x - i + W) / W));
      if (reduce.matches) e = i <= c ? 1 : 0;
      const vis = i === c || (i === c + 1 && e > 0);
      f.style.visibility = vis ? 'visible' : 'hidden';
      f.style.zIndex = String(i + 1);
      f.style.transform = `translate3d(0,${((1 - e) * 100).toFixed(2)}%,0)`;
      const m = f.querySelector('video, img');
      if (m) m.style.transform = reduce.matches ? 'none' : `translate3d(0,${(-(1 - e) * 100).toFixed(2)}%,0) scale(${(1.08 - 0.08 * ios(cl((x - i + W) / (1 + W)))).toFixed(4)})`;
      const v = f.querySelector('video');
      if (v) {
        const play = vis && this.inView && !reduce.matches;
        if (play && v.paused) { if (!f._was) v.currentTime = 0; v.muted = true; v.play().catch(() => {}); }
        else if (!play && !v.paused) v.pause();
        f._was = vis;
      }
    });
    const u = Math.min(n - 1, Math.floor(x + 0.15));
    const list = this.querySelectorAll(`.zr-c[data-ch="${this.ch}"] .zr-s`);
    list.forEach((s, k) => { s.classList.toggle('on', k === u); s.classList.toggle('done', k < u); s.setAttribute('aria-current', k === u ? 'step' : 'false'); });
  }

  // phone
  setTab(c) {
    if (c === this.mch) return;
    const cur = this.querySelector('.zrm-p:not([hidden])');
    const apply = () => {
      this.mch = c;
      this.querySelectorAll('.zrm-tab').forEach((t) => { const on = +t.dataset.ch === c; t.classList.toggle('on', on); t.setAttribute('aria-selected', String(on)); t.tabIndex = on ? 0 : -1; });
      this.querySelectorAll('.zrm-p').forEach((p) => { p.hidden = +p.dataset.ch !== c; });
      const tr = this.querySelector(`.zrm-p[data-ch="${c}"] .zrm-track`);
      if (tr) { tr.scrollLeft = 0; this.onTrack(tr); }
    };
    if (reduce.matches || !cur) return apply();
    // cross-fade 150ms out + 150ms in; the new carousel starts at card 1
    clearTimeout(this._tt); cur.style.opacity = '0';
    this._tt = setTimeout(() => {
      apply(); cur.style.opacity = '';
      const nx = this.querySelector(`.zrm-p[data-ch="${c}"]`); nx.style.opacity = '0';
      requestAnimationFrame(() => requestAnimationFrame(() => { nx.style.opacity = ''; }));
    }, 150);
  }
  onTrack(tr) {
    const cards = [...tr.querySelectorAll('.zrm-card')]; if (!cards.length) return;
    const x = tr.scrollLeft; let k = 0;
    cards.forEach((c, i) => { if (c.offsetLeft - cards[0].offsetLeft <= x + c.offsetWidth * 0.4) k = i; });
    cards.forEach((c, i) => c.classList.toggle('on', i === k));
    tr.parentElement.querySelectorAll('.zrm-dots i').forEach((d, i) => d.classList.toggle('on', i === k));
    this.playPhone();
  }
  playPhone() {
    const phone = !desk.matches;
    this.querySelectorAll('.zrm-card').forEach((c) => {
      const v = c.querySelector('video'); if (!v) return;
      const on = phone && this.inView && !reduce.matches && c.classList.contains('on') && !c.closest('.zrm-p').hidden;
      if (on && v.paused) { v.currentTime = 0; v.muted = true; v.play().catch(() => {}); }
      else if (!on && !v.paused) v.pause();
    });
  }
}
if (!customElements.get('ritual-scroll')) customElements.define('ritual-scroll', RitualScroll);
